import { IAddress } from '@carm/shared/components/form-elements/models';
import { IBaseResponse, IErrorResponse } from '@carm/_utils/models';
import {
  BusinessProgramIdentifier,
  IBn9RegistrationModel,
  RegistrationProgramType,
  TransactionQuestion,
} from '.';
import { IProgramAddressResponse } from '@carm/_core';
import { IBaseAddressResponse } from '@carm/_core/access-control-mgt/models/address-api.model';
import { IValidateBusinessTransactionsModel } from './register-business.model';
import { AddressCodeType } from '@carm/_core/address/models';

export interface IBusinessRegistrationDraft extends IErrorResponse, IValidateBusinessTransactionsModel {
  bn9: string;
  businessLegalName: string;
  businessOperatingName: string;
  caseNumber: string;
  draft: string;
  lastModifiedDate: string;
  name: string;
  status: string;
  type: string;
  uid: string;
  scenario: string;
  hasSystemErrors: string;
  legalEntityValidationStatus?: boolean;
  programNumber?: string;
  programType?: string;
  programSubType?: string;
  cbsaLegacyProgramId?: string;
  submittedDate?: string;
}

export interface IBusinessRegistrationDraftResponse extends IErrorResponse {
  businessRegistration: IBusinessRegistrationDraft;
}

export interface IBusinessRegistrationStatus {
  bn9: string;
  businessLegalName: string;
  businessOperatingName: string;
  caseNumber: string;
  draft: string;
  lastModifiedDate: Date;
  name: string;
  status: string;
  type: string;
  uid: string; // user21@email.com-000000000006-16007837507"
  scenario: string;
  hasSystemErrors: string;
}

export class IBn9RegistrationDraftRequestModel {
  uid: string; // will be undefined for a new draft
  draft: string; // model stringified as json
  businessLegalName: string;
  name?: string; // hardcoded, property required by API, name is irrelevant
  type = 'WEB_FORM'; // hardcoded, required by API
  scenario: string;
}

export interface ILegalEntitySubmitModel {
  businessStructure: string;
  incorporationCertificateId?: string;
  incorporationDate?: string;
  jurisdiction?: string;
  jurisdictionCountry?: string;
  jurisdictionProvince?: string;
  jurisdictionState?: string;
  operationType: string;
}

export interface ILegalNameSubmitModel {
  legalName: string;
  operatingName?: string;
}

export interface IBusinessActivitySubmitModel {
  businessActivity: string;
  mainServiceProductDes1: string;
  mainServiceProductDes2: string;
  mainServiceProductDes3: string;
  percentRevProductService1: string;
  percentRevProductService2: string;
  percentRevProductService3: string;
}

export interface IAddressSubmitModel {
  town: string;
  country: string;
  line1: string;
  line2?: string;
  postalCode: string;
  region: string;
  poBox?: string;
  phone: string;
  addressId?: string;
  addressIdByExternalSystem?: string;
  careOf?: string;
  addressType?: AddressCodeType;
  correspondencePreference?: string;
  faxNumber?: string;
  languagePreference?: string;
  brAddressRemark?: string;
}

export interface IPhoneSubmitModel {
  phoneType?: string;
  deviceType?: string;
  countryCode?: string;
  cityCode?: string;
  areaCode?: string;
  phoneNumber?: string;
  phoneExtension?: string;
}

export interface IOwnershipSubmitModel {
  email: string;
  firstName: string;
  language: string;
  lastName: string;
  position: string;
  domesticPhoneList: IBusinessPhonesSubmitModel;
  internationalPhoneList: IBusinessPhonesSubmitModel;
}

export interface IBusinessAddressSubmitModel {
  businessGenericContactInfo: IGenericContactInfoSubmitModel;
  mailingAddress: IAddressSubmitModel;
  physicalAddress: IAddressSubmitModel;
  recordsAddress: IAddressSubmitModel;
  recordsInCanada?: string;
  booksRecordsAtBusiness?: string;
  sameAddressAs?: string;
  sameMailingAndPhysicalAddress?: boolean;
}

export interface IGenericContactInfoSubmitModel {
  email?: string;
  extension?: string;
  fax?: string;
  name?: string;
  phone?: string;
  website?: string;
  languagePreference?: string;
  correspondencePreference?: string;
}

export interface IBusinessPhonesSubmitModel {
  phoneList: IPhoneSubmitModel[];
}

export interface IBusinessContactsSubmitModel {
  businessGenericContactInfo?: IGenericContactInfoSubmitModel;
  businessContactList: IContactSubmitModel[];
}

export interface IBusinessOwnershipSubmitModel {
  businessOwnershipList?: IOwnershipSubmitModel[];
  authorizedOfficialsList: IContactSubmitModel[];
}

export interface IBn9RegistrationSubmitModel {
  businessNumber?: string;
  programType?: RegistrationProgramType;
  legalEntity?: ILegalEntitySubmitModel;
  businessOwnership: IBusinessOwnershipSubmitModel;
  legalName?: ILegalNameSubmitModel;
  businessAddresses?: IBusinessAddressSubmitModel;
  businessContacts: IBusinessContactsSubmitModel;
  businessActivity?: IBusinessActivitySubmitModel;
  scenario?: string;
}

export interface IContactSubmitModel {
  contactType: string;
  email: string;
  extension?: string;
  fax: string;
  firstName: string;
  language: string;
  lastName: string;
  otherContactType: string;
  telephone: string;
  title: string;
  otherContactTitle?: string;
  authorizedOfficialTitle?: string;
}

export interface IPrePopulateBusinessRegistrationRequest extends IBaseResponse {
  preFilledForm: IBn9RegistrationModel;
}

export interface ISimilarAddress {
  physicalAddress: IProgramAddressSubmitModel;
  mailingAddress: IProgramAddressSubmitModel;
  recordsAddress: IProgramAddressSubmitModel;
  name: string;
}

export interface ISimilarProgramAddress {
  addressWsDto: IProgramAddressResponse[];
}

export interface IProgramAddressSubmitModel {
  addressIdByExternalSystem?: string;
  addressId?: string;
  addressType: string;
  careOf?: string;
  line1: string;
  line2?: string;
  country: string;
  region?: string;
  town?: string;
  postalCode?: string;
  poBox?: string;
  phone?: string;
  phoneExt?: string;
  faxNumber?: string;
  brAddressRemark: string;
  languagePreference?: string;
}

export interface IBaseAddressSubmitModel {
  address: IBaseAddressResponse;
  booksRecordsInCanada: boolean;
  booksRecordsAtBusiness: boolean;
}

export interface ITransactionInfoSubmitModel {
  businessNumber?: string;
  programNumber?: string;
  programId?: string;
  programType?: string;
}

export interface ILinkBusinessSubmitModel {
  legalAddress: IAddress;
  programNumber: string;
  programId: string;
  businessNumber: string;
  legalName: string;
  verificationPin: string;
  questions?: {
    questions: ITransactionQuestion[];
  };
}

export interface IValidateBusinessTransactionsSubmitModel {
  businessNumber: string;
  programNumber: string;
  programId: string;
  questions: ITransactionQuestion[];
}

export interface ITransactionQuestion {
  id: TransactionQuestion;
  amount?: string;
  date?: string;
  transactionId?: string;
}

export interface IValidateBusinessInformationSubmitModel
  extends BusinessProgramIdentifier {
  scenario: string;
  legalEntityName: string;
  businessStructure?: string;
  jurisdiction?: string;
  jurisdictionProvince?: string;
  incorporationCertificateId?: string;
  address: {
    line1: string;
    line2: string;
    town: string;
    country: string;
    region: string;
    postalCode: string;
  };
  certification: boolean;
}
